import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getRouteApi } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

import {
  useGetOccupancyData,
  useGetScenario,
  useGetSimulationRunCountData,
  useGetSimulationRunShipVisits,
  useGetSimulationRunTeuData,
  useGetYard,
} from '@/api/generated';
import { parseCutoffMs } from '@/components/blocks/berth-plan/berth-plan.utils';
import { HeaderActions } from '@/components/blocks/layout/header-actions';
import { LoadingErrorContentWrapper } from '@/components/blocks/loading-error-content-wrapper';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useFileDownload } from '@/hooks/use-file-download';
import { formatDateForDisplay, formatTimestampForFilename } from '@/lib/utils';
import { SimulationContainersCountChart } from '@/routes/(app)/(scenarios)/(simulation)/components/simulation-containers-count-chart';
import { SimulationContainersTeuChart } from '@/routes/(app)/(scenarios)/(simulation)/components/simulation-containers-teu-chart';
import { SimulationOccupancyChart } from '@/routes/(app)/(scenarios)/(simulation)/components/simulation-occupancy-chart';
import { SimulationShipVisitsCard } from '@/routes/(app)/(scenarios)/(simulation)/components/simulation-ship-visits-card';
import { SimulationYardMovesChart } from '@/routes/(app)/(scenarios)/(simulation)/components/simulation-yard-moves-chart';

const route = getRouteApi(
  '/app/scenarios/$scenarioId/simulations/$simulationId'
);

const SimulationPage: FC = () => {
  const { t } = useTranslation();
  const { scenarioId, simulationId } = route.useParams();

  const {
    data: scenario,
    error: scenarioError,
    isLoading: isScenarioLoading,
  } = useGetOccupancyData(scenarioId, {
    query: {
      enabled: !!scenarioId && !!simulationId,
    },
  });

  const {
    data: countData,
    isLoading: isCountLoading,
    error: countError,
  } = useGetSimulationRunCountData(simulationId, {
    query: {
      enabled: !!simulationId,
    },
  });

  const {
    data: teuData,
    isLoading: isTeuLoading,
    error: teuError,
  } = useGetSimulationRunTeuData(simulationId, {
    query: {
      enabled: !!simulationId,
    },
  });

  const {
    data: shipVisitsData,
    isLoading: isShipVisitsLoading,
    error: shipVisitsError,
  } = useGetSimulationRunShipVisits(simulationId, {
    query: { enabled: !!simulationId },
  });

  // The yard's last gate-in splits history from the plan, as in the editor.
  const { data: scenarioDetails } = useGetScenario(scenarioId, {
    query: { enabled: !!scenarioId },
  });
  const yardId = scenarioDetails?.yard_id ?? '';
  const { data: yard } = useGetYard(yardId, {
    query: { enabled: !!yardId },
  });
  const cutoffMs = parseCutoffMs(yard?.last_time_in);

  const simulation = useMemo(
    () => scenario?.runs.find((run) => run.simulation_run_id === simulationId),
    [scenario, simulationId]
  );

  const runIndex =
    scenarioDetails?.simulation_runs?.indexOf(simulationId) ?? -1;
  const title =
    scenarioDetails && runIndex !== -1
      ? t(scenarioDetails.name ? 'simulation.titleNamed' : 'simulation.title', {
          name: scenarioDetails.name,
          number: runIndex + 1,
          date: formatDateForDisplay(scenarioDetails.creation_date ?? ''),
        })
      : undefined;

  const { download: downloadContainerHistory, isDownloading } = useFileDownload(
    {
      url: `/simulation-runs/${simulationId}/container-history`,
      filename: `container_history_${formatTimestampForFilename()}.xlsx`,
      params: { format: 'excel' },
      onError: () => toast.error(t('general.toast.somethingWentWrong')),
    }
  );

  const hasError =
    !!scenarioError || !!countError || !!teuError || !!shipVisitsError;
  const isLoading =
    isScenarioLoading || isCountLoading || isTeuLoading || isShipVisitsLoading;
  const hasData = !!simulation && !!countData && !!teuData && !!shipVisitsData;

  return (
    <>
      <HeaderActions>
        <Button
          disabled={!simulationId || isLoading || isDownloading}
          size='sm'
          onClick={downloadContainerHistory}
          variant='outline'>
          {isDownloading ? (
            <Spinner />
          ) : (
            <>
              {t('simulation.downloadContainerHistory')}
              <Download className='mr-2 size-4' />
            </>
          )}
        </Button>
      </HeaderActions>
      {title && <h1 className='text-xl font-semibold'>{title}</h1>}
      <LoadingErrorContentWrapper
        hasError={hasError}
        isLoading={isLoading}
        errorMessage={t('general.toast.fetchFailed')}
        noResults={!hasData}>
        <div className='grid grid-cols-2 gap-4'>
          {hasData && (
            <>
              <SimulationOccupancyChart simulation={simulation} />
              <SimulationContainersCountChart data={countData} />
              <SimulationContainersTeuChart data={teuData} />
              <SimulationYardMovesChart
                key={simulationId}
                simulationId={simulationId}
              />
              <SimulationShipVisitsCard
                shipVisits={shipVisitsData.ship_visits}
                cutoffMs={cutoffMs}
              />
            </>
          )}
        </div>
      </LoadingErrorContentWrapper>
    </>
  );
};

export default SimulationPage;
